import type { BlockEmployersCostSavingsCalculator } from 'types/block-employers-cost-savings-calculator';

import React, { useState } from 'react';

import Image from 'next/image';

import {
  Wrap,
  Form,
  Button,
  SelectFieldMinimal,
  FieldWrap,
  Paragraph,
  TextFieldControlled as Input,
} from '@leafwell/components';

import HeadingBody from './heading-body';
import { useEventsTracker } from 'contexts/eventsTracker';

const RatioList = [
  {
    label: '25% Individual / 75% Family',
    value: '0.25',
  },
  {
    label: '50% Individual / 50% Family',
    value: '0.5',
  },
  {
    label: '75% Individual / 25% Family',
    value: '0.75',
  },
];

const EngagementList = [
  {
    label: '10%',
    value: '0.1',
  },
  {
    label: '20%',
    value: '0.2',
  },
  {
    label: '30%',
    value: '0.3',
  },
  {
    label: '40%',
    value: '0.4',
  },
  {
    label: '50%',
    value: '0.5',
  },
];

const CostSavingsForm: React.FC<{ instanceId?: string | null }> = ({
  instanceId,
}) => {
  const { trackEvent } = useEventsTracker();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [ratio, setRatio] = useState<string>('');
  const [engagement, setEngagement] = useState<string>('');

  const [costSavings, setCostSavings] = useState({
    savings: '0',
    lowerRange: '0',
    upperRange: '0',
  });

  const onSubmit = ({ employees }, { clearErrors, setError }) => {
    setIsLoading(true);
    clearErrors();

    if (!employees) {
      setError('employees', {
        type: 'client',
        message: 'Please insert a valid number',
      });
      setIsLoading(false);
      return;
    }

    if (!engagement) {
      setError('engagement', {
        type: 'client',
        message: 'Please select a valid value',
      });
      setIsLoading(false);
      return;
    }

    if (!ratio) {
      setError('ratio', {
        type: 'client',
        message: 'Please select a valid ratio',
      });
      setIsLoading(false);
      return;
    }

    const individualCost = 6296;
    const familyCost = 19276;

    const engagedEmployees = parseFloat(employees) * parseFloat(engagement);
    const costInd = engagedEmployees * parseFloat(ratio) * individualCost;
    const costFam = engagedEmployees * (1 - parseFloat(ratio)) * familyCost;
    const totalCost = costInd + costFam;

    const savings = totalCost * 0.04;
    const savingsMin = totalCost * 0.032;
    const savingsMax = totalCost * 0.048;

    setCostSavings({
      savings: savings.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      lowerRange: savingsMin.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      upperRange: savingsMax.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    });

    setIsLoading(false);
    setFormSubmitted(true);
  };

  return (
    <div className="grid gap-y-6 lg:max-w-lg bg-white p-6 rounded-2xl">
      <p className="text-sm">
        Tell us your employee size and select your desired criteria to see the
        estimated cost savings.*
      </p>
      <Form
        className="gap-y-4"
        mode="onSubmit"
        defaultValues={{
          employees: 100,
          engagement: engagement,
          ratio: ratio,
        }}
        onSubmit={onSubmit}
      >
        <div className="grid gap-y-2 relative">
          <p className="font-medium">Number of employees</p>
          <Input
            className="rounded-lg"
            name="employees"
            placeholder="Number of employees"
            type="number"
            min={1}
            max={99999}
            validations={['required']}
          />
        </div>
        <div className="grid gap-y-2 relative">
          <p className="font-medium">Expected employee engagement</p>
          <FieldWrap name="engagement">
            <SelectFieldMinimal
              aria-label="Expected employee engagement"
              placeholder="Employee engagement"
              options={EngagementList}
              disabled={false}
              onSelect={target => {
                setEngagement(target.value);
                setFormSubmitted(false);
              }}
            />
          </FieldWrap>
        </div>
        <div className="grid gap-y-2 relative">
          <p className="font-medium">
            Ratio of health insurance plan types (Individual vs. Family)
          </p>
          <FieldWrap name="ratio">
            <SelectFieldMinimal
              aria-label="Ratio of health insurance plan types"
              placeholder="Ratio of health insurance plan"
              options={RatioList}
              disabled={false}
              onSelect={target => {
                setRatio(target.value);
                setFormSubmitted(false);
              }}
            />
          </FieldWrap>
        </div>
        <div className="mt-2">
          <Button
            disabled={false}
            type="submit"
            label="Calculate your savings"
            variant="primary"
            className="w-full !text-lg rounded-lg font-medium font-sans"
            loading={isLoading}
            onClick={() => {
              trackEvent({
                event: 'select_content',
                content_type: 'Calculate your savings',
                item_id: `CostSavingsCalculator-${instanceId}`,
                action: 'CostSavingsCalculator-calculate-savings',
              });
            }}
          />
        </div>
      </Form>
      {formSubmitted && parseFloat(costSavings?.savings) > 0 ? (
        <>
          <p className="text-xl text-center">
            You could save <strong>${costSavings?.savings}</strong> per year!
          </p>
          <p className="text-xs text-center">Range: ±20%</p>
          <div className="relative h-1.5 bg-gray-400 rounded-md -mt-4">
            <div className="absolute h-1.5 w-1/2 top-0 left-1/4 bg-green-600 rounded-md"></div>
            <div className="absolute top-4 left-1/4 -translate-x-1/2 p-3 sm:p-1 font-medium text-xs">
              ${costSavings?.lowerRange}
            </div>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 p-3 sm:p-1 font-medium text-xs">
              ${costSavings?.savings}
            </div>
            <div className="absolute top-4 left-3/4 -translate-x-1/2 p-3 sm:p-1 font-medium text-xs">
              ${costSavings?.upperRange}
            </div>
          </div>
          <Button
            href="#contact-us-employers-form"
            label="Enquire today"
            variant="secondary"
            className="w-full !text-lg rounded-lg font-medium font-sans mt-8"
            onClick={() => {
              trackEvent({
                event: 'select_content',
                content_type: 'Enquire today',
                item_id: `CostSavingsCalculator-${instanceId}`,
                origin: 'CostSavingsCalculator-apply-employee-benefits',
              });
            }}
          />
        </>
      ) : null}
      <p className="text-sm">
        *Estimated savings are based off the national average of individual and
        family health insurance plan costs for employers in 2024. Data is from
        the Kaiser Family Foundation's 2024 Employer Health Benefits Survey.
        Savings are based on a 3% reduction in health insurance plan costs using
        evidence published in the journal{' '}
        <em>Applied Health Economics and Health Policy</em> (Doucette et al.,
        2024)
      </p>
    </div>
  );
};

const CostSavingsCalculator: React.FC<BlockEmployersCostSavingsCalculator> = ({
  sectionId,
  instanceId,
  title,
  subtitle,
}) => {
  return (
    <section
      id={sectionId}
      className="bg-green-50 my-12 md:my-16"
      itemID={`CostSavingsCalculator-${instanceId}`}
    >
      <Wrap className="grid lg:grid-cols-2 gap-x-24 items-start">
        <div className="grid gap-y-6">
          <HeadingBody className="font-semibold" level="2" title={title} />
          <Paragraph size="large">{subtitle}</Paragraph>
          <Image
            src="/tax-calculator-employers.svg"
            width={247}
            height={194}
            alt=""
            className="mt-10"
          />
        </div>
        <CostSavingsForm instanceId={instanceId} />
      </Wrap>
    </section>
  );
};

export default CostSavingsCalculator;
